import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { displayToast } from "../../../components/app/AppToast";
import { RequestError } from "../../../../../api/src/shared/request";
import {
  apiClient,
  toJsonFile,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import type { ConversationEngine } from "../../assistant/conversationEngine";
import type {
  EnsureGptConversation,
  EnsuredConversation,
} from "./useEnsureGptConversation";

// Attaching files to a Juisci GPT conversation. The pages render what this
// returns; the agent reads the files server-side (api
// `service/conversation-documents.ts` — PDFs are OCR'd and embedded, text files
// are navigated with grep / line ranges).
//
// Upload is IMMEDIATE, not deferred to send: an attachment row is FK'd to the
// conversation and the server has to ingest it before any turn can use it. So
// picking a file on the LANDING page has to create the conversation first — that
// is what the shared `conversation` coordinator does (`useEnsureGptConversation`),
// which also lets the page swap its transport from "new" to "continue" before the
// first question is asked. The coordinator is SHARED with the playlist-reference
// hook so a file + a playlist added together create ONE conversation, not two.
//
// The attached list is NOT mirrored into local state: it is the documents query,
// refetched after every mutation. Only the in-flight uploads live here, because
// they have no server row yet — so there is exactly one source of truth for
// "what is attached", and a chip cannot survive a failed upload.

export type ChatAttachedFile = {
  status: "attached";
  documentId: string;
  filename: string;
  // `text` is navigated (grep / line ranges), `paged` is OCR'd + embedded.
  kind: "paged" | "text";
  contentType: string;
  // Null only for historical `paged` rows that predate the column. Drives the
  // preview size gate, so an unknown size is treated as "don't guess".
  byteSize: number | null;
};

export type ChatAttachment =
  // `pickId` is exposed, not internal: the list keeps concurrent same-name uploads
  // as separate rows, so the chip needs a unique React key for them — keying on
  // filename alone lets reconciliation drop or mis-update a spinner.
  { status: "uploading"; pickId: number; filename: string } | ChatAttachedFile;

export type ChatAttachments = {
  files: ChatAttachment[];
  // An upload or a delete is in flight — the composer disables sending, because
  // a question asked before the file lands would be answered without it.
  isBusy: boolean;
  onPick: (picked: FileList | null) => void;
  onRemove: (documentId: string) => void;
  /**
   * Forget everything this hook created — for the landing page's "back", which
   * abandons the turn and returns to a blank slate.
   *
   * Load-bearing rather than tidy-up. The conversation created to hold an
   * attachment must NOT survive: the server persists an abandoned mid-stream turn
   * by design (it keeps draining after the client leaves), so continuing that
   * conversation would append the user's next question to a transcript holding a
   * turn they cannot see on the landing — local state saying "fresh" while the
   * server disagrees. Also the symmetry the no-attachment path already has, where
   * back nulls the conversation id and the next question opens a new one.
   *
   * The cost is real: the upload is discarded and has to be redone. It is the
   * honest option because the chips disappear with it, so the user SEES that the
   * file is no longer attached instead of silently carrying it into a thread they
   * think is new.
   */
  reset: () => void;
  // The conversation the files belong to, or null before one exists. The preview
  // needs it to address the content route.
  conversation: EnsuredConversation | null;
  // The file whose preview is open, or null. Lives here rather than in a
  // component so the list stays presentational, and so it survives the parent
  // re-renders an open sheet causes.
  previewing: ChatAttachedFile | null;
  onPreview: (documentId: string) => void;
  onClosePreview: () => void;
};

// The api sends a message worth reading — "Unsupported file type: notes.docx",
// "Too large (max 12 MB): library.rdf" — and `StatusError.message` already
// carries it. Anything that is not a request failure (a network drop, a bug) has
// no message fit for a user, so it falls back to the generic copy.
function attachmentErrorMessage(
  error: unknown,
  t: (key: string) => string
): string {
  return error instanceof RequestError
    ? error.message
    : t("Something went wrong. Please try again.");
}

export function useChatAttachments(opts: {
  // The shared conversation coordinator (`useEnsureGptConversation`). It owns the
  // conversation identity + on-demand creation + the shared invalidation epoch,
  // so a file + a playlist added together create ONE conversation.
  conversation: EnsureGptConversation;
}): ChatAttachments {
  const { conversation } = opts;
  const { t } = useTranslation();
  // Uploads in flight are keyed by a per-PICK token, not by filename: picking the
  // same name twice (overlapping) meant the first completion's cleanup removed
  // BOTH spinners by name, so the second upload looked finished while `busyCount`
  // still blocked sending. A counter rather than a random id keeps it testable.
  const pickCounter = useRef(0);
  // Invalidation goes through the CACHE KEY, not `documentsQuery.refetch()`.
  //
  // On the landing page the first pick runs while `target` is still null, so
  // the query captured in that closure is the DISABLED one and `refetch()` on it
  // is a no-op. Meanwhile creating the conversation enables the query, which
  // immediately fetches and — the upload still being in flight, seconds for a
  // PDF — caches an EMPTY list. The upload then finished against a dead refetch,
  // so as soon as the optimistic row cleared, the next render painted no chips
  // at all while the file was in fact attached. (Focusing the composer is what
  // surfaced it: it forces the re-render that repaints the stale empty list.)
  //
  // Keying off the id we actually created sidesteps the closure entirely.
  const revalidate = useApiRevalidate();
  // Local rows for uploads in flight. `pickId` scopes them to the pick that
  // started them; `filename` is only for display (a file has no server id yet).
  const [uploading, setUploading] = useState<
    Array<{ pickId: number; filename: string }>
  >([]);
  const [busyCount, setBusyCount] = useState(0);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const target = conversation.target;

  // One query per engine; the other stays idle.
  const legacyDocumentsQuery = useApiQuery(
    "/webapp/private/conversations/:id/documents",
    target === null || target.engine !== "legacy"
      ? false
      : { param: { id: target.id } }
  );
  const assistantDocumentsQuery = useApiQuery(
    "/assistant/conversations/:conversationId/documents",
    target === null || target.engine !== "assistant"
      ? false
      : { param: { conversationId: target.id } }
  );
  const documents =
    legacyDocumentsQuery.state === "success"
      ? legacyDocumentsQuery.body
      : assistantDocumentsQuery.state === "success"
        ? assistantDocumentsQuery.body.documents
        : [];
  const attached: ChatAttachedFile[] = documents.map((document) => ({
    status: "attached",
    documentId: document.id,
    filename: document.filename,
    kind: document.kind,
    contentType: document.contentType,
    byteSize: document.byteSize,
  }));
  const revalidateDocuments = (id: string, engine: ConversationEngine) =>
    engine === "assistant"
      ? revalidate("/assistant/conversations/:conversationId/documents", {
          param: { conversationId: id },
        })
      : revalidate("/webapp/private/conversations/:id/documents", {
          param: { id },
        });
  // Derived, never stored: a file detached while its preview is open simply
  // stops being previewable, with no second copy of the truth to reconcile.
  const previewing =
    previewingId === null
      ? null
      : (attached.find((file) => file.documentId === previewingId) ?? null);

  const onPick = (picked: FileList | null): void => {
    if (picked === null || picked.length === 0) return;
    const chosen = [...picked];
    pickCounter.current += 1;
    const pickId = pickCounter.current;
    setUploading((current) => [
      ...current,
      ...chosen.map((file) => ({ pickId, filename: file.name })),
    ]);
    setBusyCount((count) => count + 1);
    const epoch = conversation.epoch();

    void (async () => {
      try {
        const ensured = await conversation.ensure(epoch);
        if (conversation.isStale(epoch)) return;

        const files = await Promise.all(chosen.map((file) => toJsonFile(file)));
        const { rejected } =
          ensured.engine === "assistant"
            ? (
                await apiClient.post(
                  "/assistant/conversations/:conversationId/documents",
                  { param: { conversationId: ensured.id }, json: { files } }
                )
              ).body
            : (
                await apiClient.post(
                  "/webapp/private/conversations/:id/documents",
                  { param: { id: ensured.id }, json: { files } }
                )
              ).body;
        if (conversation.isStale(epoch)) return;
        if (rejected.length > 0) {
          displayToast(
            t("Couldn't read {{files}}", {
              files: rejected.map((rejection) => rejection.filename).join(", "),
            }),
            "warning"
          );
        }
        // The documents query owns the attached list — invalidate rather than
        // splicing the response in, so the chips can never drift from the
        // conversation's real context. AWAITED, so the cache holds the new file
        // before the optimistic row is cleared below and there is no frame with
        // neither.
        await revalidateDocuments(ensured.id, ensured.engine);
      } catch (error) {
        // An abandoned pick reports nothing: the user has moved on, and a failure
        // toast for work they walked away from is noise.
        if (conversation.isStale(epoch)) return;
        displayToast(attachmentErrorMessage(error, t), "error");
      } finally {
        // Only if this pick is still current: `reset` already zeroed the counter
        // and cleared the rows, so decrementing here would drive `busyCount`
        // negative and leave `isBusy` wrong for every later pick.
        if (!conversation.isStale(epoch)) {
          // Clears whether it worked or not: on success the query now carries
          // these files, on failure they are not attached at all. Either way the
          // optimistic row has served its purpose. Only THIS pick's rows — a
          // concurrent pick of the same filename keeps its own spinner.
          setUploading((current) =>
            current.filter((row) => row.pickId !== pickId)
          );
          setBusyCount((count) => count - 1);
        }
      }
    })();
  };

  const onRemove = (documentId: string): void => {
    if (target === null)
      throw new Error(
        "should not happen: removing an attachment with no conversation"
      );
    // Same epoch discipline as a pick: `reset` bumps the shared epoch, so an
    // unguarded decrement here would drive it negative and the NEXT pick would
    // read `isBusy === false` while its file was still uploading — enabling send
    // before the attachment exists.
    const epoch = conversation.epoch();
    setBusyCount((count) => count + 1);
    void (async () => {
      try {
        if (target.engine === "assistant")
          await apiClient.delete(
            "/assistant/conversations/:conversationId/documents/:documentId",
            { param: { conversationId: target.id, documentId } }
          );
        else
          await apiClient.delete(
            "/webapp/private/conversations/:id/documents/:documentId",
            { param: { id: target.id, documentId } }
          );
        if (conversation.isStale(epoch)) return;
        await revalidateDocuments(target.id, target.engine);
      } catch (error) {
        if (conversation.isStale(epoch)) return;
        displayToast(attachmentErrorMessage(error, t), "error");
      } finally {
        if (!conversation.isStale(epoch)) setBusyCount((count) => count - 1);
      }
    })();
  };

  const reset = (): void => {
    // Anything in flight becomes a no-op from here (the shared epoch bumps in
    // `conversation.reset()`), and the busy counter is zeroed rather than
    // decremented. That zero is only correct because EVERY async path that
    // touches `busyCount` skips its decrement on a stale epoch — if you add
    // another one, guard it too or this goes negative.
    setBusyCount(0);
    setPreviewingId(null);
    setUploading([]);
    // Bump the shared epoch + forget the created conversation (idempotent if the
    // page also resets the playlist hook — the epoch just increments again).
    conversation.reset();
  };

  return {
    files: [
      ...attached,
      ...uploading.map((row) => ({
        status: "uploading" as const,
        pickId: row.pickId,
        filename: row.filename,
      })),
    ],
    isBusy: busyCount > 0,
    onPick,
    onRemove,
    reset,
    conversation: target,
    previewing,
    onPreview: setPreviewingId,
    onClosePreview: () => {
      setPreviewingId(null);
    },
  };
}
